import { useParams } from "react-router-dom";
import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import z from "zod";
import { useNoticeApi } from "@/v2/entities/notice/Notice.api";
import { useQuery } from "react-query";
import { SelectSimulation } from "@/api/simulator/SelectSimulation";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NoticeOutputPopupForm,
  NoticeOutputPopupFormPayload,
} from "@/v2/features/notice/NoticeOutputPopupForm";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDebounce } from "@/v2/shared/hooks/useDebounce";
import { toast } from "sonner";
import { NoticeOutputPopup } from "@/v2/entities/notice/Notice.model";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useGetChatSimulators } from "@/v2/features/notice/useGetChatSimulators";
import { NoticeOutputPopupPreview } from "@/v2/features/notice/NoticeOutputPopupPreview";
import { PublishNoticeOutput } from "@/v2/features/notice/PublishNoticeOutput";

export function useNoticeId() {
  const { noticeId } = useParams();
  return z.string().min(1).parse(noticeId);
}

function NoticePage() {
  const noticeId = useNoticeId();
  const { getNoticeById } = useNoticeApi();

  const [simulatorId, setSimulatorId] = useState<number>();
  const [payloadToPublish, setPayloadToPublish] =
    useState<NoticeOutputPopupFormPayload>();

  const { chatSimulators, isLoading: isLoadingChatSimulators } =
    useGetChatSimulators((data) => setSimulatorId(data[0]?.id));

  const methods = useForm<NoticeOutputPopupFormPayload>({
    mode: "onChange",
    defaultValues: { title: "", message: "", ctaLabel: "" },
  });

  const { data: notice, refetch: refetchNotice } = useQuery({
    queryKey: ["notices", noticeId],
    queryFn: () => getNoticeById(noticeId),
  });

  const noticeOutputPopup = useMemo<NoticeOutputPopup | undefined>(() => {
    let noticeOutputPopup: NoticeOutputPopup | undefined;

    if (notice == null) {
      return undefined;
    }

    notice.noticeOutputs
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .forEach((output) => {
        if (output.type === "Popup") {
          noticeOutputPopup = output;
        }
      });

    return noticeOutputPopup;
  }, [notice]);

  useEffect(() => {
    if (noticeOutputPopup) {
      methods.reset({
        title: noticeOutputPopup.title,
        message: noticeOutputPopup.message,
        ctaLabel: noticeOutputPopup.ctaLabel,
      });
    } else {
      methods.reset({
        title: "",
        message: "",
        ctaLabel: "",
      });
    }
  }, [noticeOutputPopup, methods]);

  const handleSelectSimulator = useCallback(
    (simulatorId: number) => {
      setSimulatorId(simulatorId);
    },
    [setSimulatorId]
  );

  const handlePublish = useCallback(
    methods.handleSubmit((data) => {
      const payload: NoticeOutputPopupFormPayload = {
        title: data.title,
        message: data.message,
        ...(data.ctaLabel?.trim() ? { ctaLabel: data.ctaLabel.trim() } : {}),
      };

      setPayloadToPublish(payload);
    }),
    [methods, noticeOutputPopup]
  );

  const handlePublishSuccess = useCallback(async () => {
    await refetchNotice();
    setPayloadToPublish(undefined);
    toast.success("Notice published successfully");
  }, [refetchNotice]);

  // Watch form fields for live preview
  const formTitle = useWatch({ control: methods.control, name: "title" });
  const formMessage = useWatch({ control: methods.control, name: "message" });
  const formCtaLabel = useWatch({ control: methods.control, name: "ctaLabel" });

  // Debounce to prevent excessive re-renders (300ms)
  const debouncedTitle = useDebounce(formTitle, 300);
  const debouncedMessage = useDebounce(formMessage, 300);
  const debouncedCtaLabel = useDebounce(formCtaLabel, 300);

  // Memoize preview payload
  const previewData = useMemo(
    () => ({
      title: debouncedTitle || "",
      message: debouncedMessage || "",
      ctaLabel: debouncedCtaLabel,
    }),
    [debouncedTitle, debouncedMessage, debouncedCtaLabel]
  );

  if (notice != null && payloadToPublish != null) {
    return (
      <PageLayoutV2 className="w-full">
        <Flex direction="column" height="100%">
          <Flex className="h-12 px-4 justify-between items-center border-b">
            <Flex align="center" gap="2">
              <Text size="2">{notice?.name}</Text>

              <Badge color="gray" variant="soft" size="1">
                {notice?.version}
              </Badge>

              <ChevronRightIcon />

              <Text size="2" weight="medium" highContrast>
                {payloadToPublish.title}
              </Text>
            </Flex>

            <Flex align="center" gap="2">
              <Button
                variant="soft"
                size="2"
                color="gray"
                highContrast
                onClick={() => setPayloadToPublish(undefined)}
              >
                Discard
              </Button>
            </Flex>
          </Flex>

          <Flex flexGrow="1">
            <Flex
              flexGrow="1"
              p="8"
              direction="column"
              justify="between"
              align="center"
            >
              <PublishNoticeOutput
                notice={notice}
                payloadToPublish={payloadToPublish}
                onPublish={handlePublishSuccess}
              />
            </Flex>
          </Flex>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <FormProvider {...methods}>
      <PageLayoutV2 className="w-full">
        <Flex direction="column" height="100%">
          <Flex className="h-12 px-4 justify-between items-center border-b">
            <Flex align="center" gap="2">
              <Text size="2" weight="medium" highContrast>
                {notice?.name}
              </Text>

              <Badge color="gray" variant="soft" size="1">
                {notice?.version}
              </Badge>
            </Flex>

            <Flex align="center" gap="2">
              <Button
                variant="solid"
                size="2"
                color="gray"
                highContrast
                onClick={handlePublish}
              >
                Publish
              </Button>
            </Flex>
          </Flex>

          <Flex flexGrow="1">
            <Flex
              flexGrow="1"
              p="8"
              direction="column"
              justify="between"
              align="center"
            >
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium" highContrast>
                  Preview in Simulator
                </Text>
                <div className="w-[452px]">
                  <SelectSimulation
                    size="medium"
                    placeholder="Select simulator"
                    simulators={chatSimulators}
                    value={simulatorId}
                    onChange={handleSelectSimulator}
                    disabled={isLoadingChatSimulators}
                  />
                </div>
              </Flex>

              <NoticeOutputPopupPreview
                simulatorId={simulatorId}
                previewData={previewData}
              />
            </Flex>

            <Flex
              width="360px"
              p="4"
              direction="column"
              gap="2"
              className="border-l w-[400px]"
            >
              <Flex py="6" direction="column" gap="2" align="center">
                <Text size="5" weight="bold" highContrast>
                  {notice?.name}
                </Text>

                <Badge color="gray" variant="soft" size="2">
                  {notice?.version}
                </Badge>
              </Flex>

              <form onSubmit={(e) => e.preventDefault()}>
                <NoticeOutputPopupForm />
              </form>
            </Flex>
          </Flex>
        </Flex>
      </PageLayoutV2>
    </FormProvider>
  );
}

export default NoticePage;
